/**
 * Tiny in-memory sender for current marketing-session consent.
 *
 * Consent decisions are forwarded to the backend so it can enforce delivery gates. This module
 * buffers the latest decision per journey until its transport mounts and retries with capped
 * backoff. A newer decision supersedes earlier pending values, including failed grants.
 */

import { withAnalyticsTransportDeadline } from "@/lib/analytics/analyticsTransportDeadline";
import { createRequestCorrelationId } from "@/lib/analytics/correlation";
import { getMarketingSessionId } from "@/lib/analytics/marketingSession";
import { getConsentState } from "@/lib/consent/cookiebar";
import { logger } from "@/lib/logging/logger";

export type WebAnalyticsConsentObservation = {
  observationId: string;
  marketingSessionId: string;
  observedAt: string;
  consent: {
    analytics: boolean;
    advertisement: boolean;
  };
};

type MarketingObservationResult = { status: "stored" | "duplicate" | "dropped" };

type PendingMarketingConsentObservation = {
  input: WebAnalyticsConsentObservation;
  failureCount: number;
  dropCount: number;
  /** Set while this observation is serving out its own backoff; only IT is held back by it. */
  retryTimer?: ReturnType<typeof globalThis.setTimeout>;
};

type MarketingObservationTransport = {
  recordConsent: (input: WebAnalyticsConsentObservation, signal?: AbortSignal) => Promise<MarketingObservationResult>;
};

export const MARKETING_STATE_RETRY_DELAY_MS = 1_000;
const MARKETING_STATE_MAX_RETRY_DELAY_MS = 30_000;
/**
 * How many times a `dropped` acknowledgement is retried before the value is released.
 *
 * The API drops an observation for reasons that are usually a losable race rather than a verdict:
 * the journey row is not visible to this request yet, or the device owner changed mid-flight.
 * Transport failures may retry for the life of the document because nothing rejected the value;
 * a drop is bounded because some causes (a skewed client clock, a journey that has moved on) will
 * never succeed for this exact observation. Four attempts spans roughly seven seconds of backoff.
 */
const MARKETING_STATE_MAX_DROP_ATTEMPTS = 4;

const log = logger.child("MarketingSessionState");
const pendingObservations = new Map<string, PendingMarketingConsentObservation>();
let activeObservationId: string | undefined;
const latestConsentKeyBySession = new Map<string, string>();
let transport: MarketingObservationTransport | undefined;
let lastObservedAtEpochMs = 0;

function nextObservedAt(): string {
  lastObservedAtEpochMs = Math.max(Date.now(), lastObservedAtEpochMs + 1);
  return new Date(lastObservedAtEpochMs).toISOString();
}

/** Mounts the tRPC transport and immediately sends state observed during bootstrap. */
export function configureMarketingObservations(nextTransport: MarketingObservationTransport): () => void {
  transport = nextTransport;
  sendNextObservation();
  return () => {
    if (transport !== nextTransport) return;
    transport = undefined;
    for (const pending of pendingObservations.values()) clearRetryTimer(pending);
  };
}

/** Queues a local CMP decision. */
export function observeWebAnalyticsConsent(consent: { analytics: boolean; advertisement: boolean }): void {
  const marketingSessionId = getMarketingSessionId();
  const key = `${consent.analytics ? "1" : "0"}:${consent.advertisement ? "1" : "0"}`;
  if (latestConsentKeyBySession.get(marketingSessionId) === key) return;
  latestConsentKeyBySession.set(marketingSessionId, key);

  enqueueObservation({
    observationId: createRequestCorrelationId(),
    marketingSessionId,
    observedAt: nextObservedAt(),
    consent: { ...consent },
  });
}

/** Reads and forwards the current CMP decision, if the banner has reached a decision. */
export function observeCurrentWebAnalyticsConsent(): void {
  // Resolve the journey before snapshotting consent. getMarketingSessionId() can synchronously
  // adopt another tab's replacement journey and fire identity-boundary listeners that reconcile
  // consent from the shared cookie; reading getConsentState() only after that boundary settles
  // ensures the forwarded tuple reflects the reconciliation instead of a pre-boundary tab-local
  // grant being attached to the new journey with a newer observedAt.
  getMarketingSessionId();
  const consent = getConsentState();
  if (!consent.initialized) return;
  observeWebAnalyticsConsent({
    analytics: consent.analytics,
    advertisement: consent.advertisement,
  });
}

function enqueueObservation(input: WebAnalyticsConsentObservation): void {
  const previous = pendingObservations.get(input.marketingSessionId);
  if (previous) clearRetryTimer(previous);
  pendingObservations.set(input.marketingSessionId, {
    input,
    failureCount: 0,
    dropCount: 0,
  });
  sendNextObservation();
}

function sendNextObservation(): void {
  const activeTransport = transport;
  if (activeObservationId !== undefined || !activeTransport) return;

  const pending = selectSendableObservation();
  if (!pending) return;
  const { observationId } = pending.input;

  activeObservationId = observationId;
  void performSend(observationId, pending, activeTransport);
}

/** Backoff belongs to one journey; an older journey must not delay the current one's consent. */
function selectSendableObservation(): PendingMarketingConsentObservation | undefined {
  for (const pending of pendingObservations.values()) {
    if (pending.retryTimer === undefined) return pending;
  }
  return undefined;
}

async function performSend(
  observationId: string,
  pending: PendingMarketingConsentObservation,
  activeTransport: MarketingObservationTransport,
): Promise<void> {
  const { marketingSessionId } = pending.input;
  try {
    const result = await withAnalyticsTransportDeadline((signal) =>
      activeTransport.recordConsent(pending.input, signal),
    );
    // A late response or failure for an older value must never remove or retry its replacement.
    // Server observation timestamps prevent an older request from overwriting a newer decision.
    if (pendingObservations.get(marketingSessionId) !== pending) return;
    if (result.status !== "dropped") {
      pendingObservations.delete(marketingSessionId);
      return;
    }
    // A drop means canonical consent state did NOT change. Dropping the observation here too
    // would leave that journey's consent unknown for the rest of its life — and unknown consent
    // is fail-closed at delivery, so every provider row on it is eventually skipped.
    pending.dropCount += 1;
    if (pending.dropCount < MARKETING_STATE_MAX_DROP_ATTEMPTS) {
      log.warn("Marketing consent state update was dropped; retrying", {
        attributes: { dropCount: pending.dropCount },
      });
      scheduleRetry({ pending, attempt: pending.dropCount });
      return;
    }
    pendingObservations.delete(marketingSessionId);
    // Release the per-journey suppression so the same values can be observed again later (a CMP
    // reconciliation on focus, visibility, or a journey boundary) with a fresh observation time,
    // instead of being silently treated as already delivered.
    latestConsentKeyBySession.delete(pending.input.marketingSessionId);
    log.warn("Marketing consent state update was dropped repeatedly; releasing it for re-observation", {
      attributes: { dropCount: pending.dropCount },
    });
  } catch (error) {
    if (pendingObservations.get(marketingSessionId) !== pending) return;
    pending.failureCount += 1;
    log.warn("Marketing consent state update failed; retrying", {
      attributes: {
        failureCount: pending.failureCount,
        errorType: error instanceof Error ? error.name : typeof error,
      },
    });
    scheduleRetry({ pending, attempt: pending.failureCount });
  } finally {
    if (activeObservationId === observationId) activeObservationId = undefined;
    sendNextObservation();
  }
}

function scheduleRetry({ pending, attempt }: { pending: PendingMarketingConsentObservation; attempt: number }): void {
  if (pending.retryTimer !== undefined || !transport) return;
  const delay = Math.min(
    MARKETING_STATE_RETRY_DELAY_MS * 2 ** Math.min(attempt - 1, 30),
    MARKETING_STATE_MAX_RETRY_DELAY_MS,
  );
  pending.retryTimer = globalThis.setTimeout(() => {
    clearRetryTimer(pending);
    sendNextObservation();
  }, delay);
}

function clearRetryTimer(pending: PendingMarketingConsentObservation): void {
  if (pending.retryTimer !== undefined) globalThis.clearTimeout(pending.retryTimer);
  pending.retryTimer = undefined;
}

/** Test-only reset; production state intentionally lasts for the document lifetime. */
export function resetMarketingObservationsForTests(): void {
  for (const pending of pendingObservations.values()) clearRetryTimer(pending);
  pendingObservations.clear();
  activeObservationId = undefined;
  transport = undefined;
  latestConsentKeyBySession.clear();
  lastObservedAtEpochMs = 0;
}
